import { useQuery } from "react-query";
import { getClinianClientsWithFilters } from "api/clinician";
import { GET_CLINICIAN_CLIENTS, STALE_TIME_5_MINUTES } from "@constants/reactQueryKeys";
import { useLocation } from "react-router-dom";
import { useMemo } from "react";

/**
 * @description `useGetClinicianClients` is a hook that fetches a clinician's clients using a GET request to
 * `/v3/clinician/[userId]/clients` endpoint.
 * @param {string} userId - The user ID
 * @param {Object} query - The query parameters
 * @returns {Object} The clinician clients
 */
const useGetClinianClientsWithFilters = (userId, query = {}) => {
  const location = useLocation();

  const finalQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const hasCreatedDateParam = params.has("created_date");

    // Only override sort; keep everything else intact
    return hasCreatedDateParam ? { ...query, sort: "u.created_date" } : query;
  }, [location.search, query]);

  const { data, isLoading, ...rest } = useQuery(
    [GET_CLINICIAN_CLIENTS, userId, finalQuery],
    () => getClinianClientsWithFilters(userId, finalQuery),
    {
      enabled: Boolean(userId),
      refetchOnWindowFocus: false,
      staleTime: STALE_TIME_5_MINUTES,
    }
  );

  return {
    clinicianClients: data?.data?.clients || [],
    clientsCountList: data?.data?.clientsCountList || [],
    insuranceCountList: data?.data?.insuranceCountList || [],
    clinicianCountList: data?.data?.clinicianCountList || [],
    supervisorCountList: data?.data?.supervisorCountList || [],
    statusCountList: data?.data?.statusCountList || [],
    totalCount: data?.data?.count || 0,
    clinicianClientsLoading: isLoading,
    ...rest,
  };
};

export default useGetClinianClientsWithFilters;
